import clsx from 'clsx'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { ActionButtonType, IMainCarouselItem } from '../../../api/personas'
import { AnimatedSpinner } from '../../../components/AnimatedSpinner'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import {
  fetchMentorProfileBySlug,
  generateMarketingImage,
} from '../../api/apiHooks'
import { FormInput } from '../FormInput'
import { IconButton } from '../IconButton'
import { DownloadIcon } from '../icons'
import { ImageUploader } from '../ImageUpload'
import { Separator } from '../Separator'
import { VideoUploader } from '../VideoUpload'
import { ImageGenerationModal } from './ImageGenerationModal'
import './styles.css'
import { generateFileFromUrl, getFileExtension } from './utils'

export interface CarouselItem extends IMainCarouselItem {
  isNewEntry?: boolean
  backgroundImageFile?: File
  backgroundVideoFile?: File
  backupBackgroundImageUrl?: string
}

export interface CarouselFormProps {
  id: string
  value: CarouselItem
  onSubmit?: (data: CarouselItem) => void
}

export function CarouselForm({ id, value, onSubmit }: CarouselFormProps) {
  const [open, setOpen] = useState(false)
  const isMobile = useMediaQuery('md')
  const [prompt, setPrompt] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [actionButtonEnabled, setActionButtonEnabled] = useState(
    !!value.actionButton,
  )
  const [actionButtonType, setActionButtonType] = useState<ActionButtonType>(
    value.actionButton?.action || 'url',
  )
  const [companionEnabled, setCompanionEnabled] = useState(!!value.companion)
  const [companionSlug, setCompanionSlug] = useState(
    value.companion?.slug || '',
  )
  const [companionData, setCompanionData] = useState(value.companion)
  const [isFetchingCompanion, setIsFetchingCompanion] = useState(false)

  const defaultValues = useMemo<CarouselItem>(() => {
    const companion = value.companion
    if (companion) {
      companion.hideJobTitle = companion.hideJobTitle ?? false
    }

    return {
      id: value.id || id,
      title:
        value.title ||
        `${value.companion?.firstName || ''} ${value.companion?.lastName || ''}${value.companion?.jobTitle ? ` - ${value.companion?.jobTitle}` : ''}`.trim(),
      description: value.description || '',
      sourceType: value.sourceType,
      visibility: value.visibility,
      backgroundImageUrl: value.backgroundImageUrl,
      backupBackgroundImageUrl: value.backgroundImageUrl,
      backgroundVideoUrl: value.backgroundVideoUrl || undefined,
      backgroundImageFile: value.backgroundImageFile,
      backgroundVideoFile: value.backgroundVideoFile,
      companion,
      banner: value.banner,
      actionButton: value.actionButton,
    }
  }, [value, id])

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<CarouselItem>({ defaultValues })

  useEffect(() => {
    reset(defaultValues)
  }, [reset, defaultValues])

  // Update form companion data when companionData changes
  useEffect(() => {
    if (companionEnabled && companionData) {
      setValue('companion', companionData, {
        shouldDirty: true,
        shouldValidate: true,
      })
    } else if (!companionEnabled) {
      setValue('companion', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [companionEnabled, companionData, setValue])

  // Ensure action button type is set in form
  useEffect(() => {
    if (actionButtonEnabled) {
      setValue('actionButton.action', actionButtonType, {
        shouldDirty: true,
      })
    }
  }, [actionButtonEnabled, actionButtonType, setValue])

  const onFormSubmit = (data: CarouselItem) => {
    onSubmit?.(data)
  }

  useEffect(() => {
    register('backgroundImageUrl', {
      required:
        value.sourceType !== 'Context' ? 'Background image is required' : false,
    })
    register('backgroundVideoUrl')
  }, [register, value.sourceType])

  const handleClose = () => {
    setPrompt('')
    setOpen(false)
  }

  const handleGenerateImage = async () => {
    if (prompt?.trim()) {
      setIsGenerating(true)
      try {
        const response = await generateMarketingImage(prompt)
        const { file, dataUrl } = await generateFileFromUrl(response.imageUrl)

        setValue('backgroundImageUrl', dataUrl, {
          shouldDirty: true,
          shouldValidate: true,
        })
        setValue('backgroundImageFile', file, {
          shouldDirty: true,
          shouldValidate: true,
        })
        handleClose()
      } catch (error) {
        console.error('Error generating image:', error)
        toast.error('Failed to generate image. Please try again later.')
      } finally {
        setIsGenerating(false)
      }
    }
  }

  const downloadFromUrl = (url: string, downloadFileName: string) => {
    const link = document.createElement('a')
    link.href = url
    link.download = downloadFileName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleFetchCompanion = async () => {
    if (!companionSlug.trim()) {
      toast.error('Please enter a companion slug')
      return
    }

    setIsFetchingCompanion(true)
    try {
      const response = await fetchMentorProfileBySlug(companionSlug.trim())
      if (response.success && response.data) {
        const mentor = response.data
        setCompanionData({
          id: mentor.mentorId,
          slug: mentor.slug,
          firstName: mentor.firstName,
          lastName: mentor.lastName,
          jobTitle: mentor.jobTitle,
          imageUrl: mentor.previewUrl,
          videoUrl: mentor.videoLoopUrl,
          hideJobTitle: false,
        })
        toast.success('Companion details fetched successfully')
      } else {
        toast.error('Companion not found')
      }
    } catch (error) {
      console.error('Error fetching companion:', error)
      toast.error('Failed to fetch companion details')
    } finally {
      setIsFetchingCompanion(false)
    }
  }

  // Watch form values
  const backgroundImageUrl = watch('backgroundImageUrl')
  const backgroundVideoUrl = watch('backgroundVideoUrl')

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4 mt-4">
        <div className="grid grid-cols-1 gap-6">
          <div className="space-y-2">
            <label
              htmlFor={`visibility-selector-${id}`}
              className="text-md font-medium text-white"
            >
              Who can see this?
            </label>
            <div
              id={`visibility-selector-${id}`}
              className="grid grid-cols-2 sm:flex sm:items-center gap-4"
            >
              <FormInput
                type="radio"
                name="visibility"
                id={`visibility-public-${id}`}
                value="public"
                register={register}
                error={errors.visibility?.message}
                label="All users"
                labelPosition="end"
                className="!flex-row !items-center"
              />

              <FormInput
                type="radio"
                name="visibility"
                id={`visibility-admin-${id}`}
                value="admin"
                register={register}
                error={errors.visibility?.message}
                label="Admins only"
                labelPosition="end"
                className="!flex-row !items-center"
              />

              <FormInput
                type="radio"
                name="visibility"
                id={`visibility-visitor-${id}`}
                value="visitor"
                register={register}
                error={errors.visibility?.message}
                label="Visitors only"
                labelPosition="end"
                className="!flex-row !items-center"
              />

              <FormInput
                type="radio"
                name="visibility"
                id={`visibility-hidden-${id}`}
                value="hidden"
                register={register}
                error={errors.visibility?.message}
                label="Hidden"
                labelPosition="end"
                className="!flex-row !items-center"
              />
            </div>
          </div>

          <FormInput
            label="Title"
            name="title"
            register={register}
            validation={{ required: 'Title is required' }}
            error={errors.title?.message}
            type="text"
          />

          <FormInput
            label="Description"
            name="description"
            register={register}
            error={errors.description?.message}
            validation={{
              required:
                defaultValues.sourceType === 'Banner'
                  ? 'Description is required'
                  : false,
            }}
            autoComplete="off"
            type="text"
          />

          <Separator text="" />

          {/* Action Button Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Action Button</h3>

            {/* Enable/Disable Checkbox */}
            <FormInput
              type="checkbox"
              id={`actionButton-enabled-${id}`}
              name="actionButton-enabled"
              checked={actionButtonEnabled}
              onChange={e => {
                const enabled = e.target.checked
                setActionButtonEnabled(enabled)
                if (!enabled) {
                  setValue('actionButton', undefined, { shouldDirty: true })
                }
              }}
              className="!flex-row !items-center !gap-2"
              label="Enable Action Button"
              labelPosition="end"
            />

            {/* Show fields only when enabled */}
            {actionButtonEnabled && (
              <div className="space-y-4">
                {/* Button Label */}
                <FormInput
                  label="Button Label"
                  name="actionButton.label"
                  register={register}
                  validation={{
                    required: actionButtonEnabled
                      ? 'Button label is required'
                      : false,
                  }}
                  error={errors.actionButton?.label?.message}
                  type="text"
                  placeholder="e.g., Watch Video, Learn More"
                />

                {/* Button Action Type */}
                <div className="space-y-2">
                  <label className="block text-md font-medium text-white">
                    Button Action
                  </label>
                  <div className="flex items-center gap-4">
                    <FormInput
                      type="radio"
                      name="actionButton.action"
                      id={`actionButton-url-${id}`}
                      value="url"
                      checked={actionButtonType === 'url'}
                      onChange={() => {
                        setActionButtonType('url')
                        setValue('actionButton.action', 'url', {
                          shouldDirty: true,
                        })
                      }}
                      register={register}
                      label="Go to URL"
                      labelPosition="end"
                      className="!flex-row !items-center"
                    />
                    <FormInput
                      type="radio"
                      name="actionButton.action"
                      id={`actionButton-video-${id}`}
                      value="video"
                      checked={actionButtonType === 'video'}
                      onChange={() => {
                        setActionButtonType('video')
                        setValue('actionButton.action', 'video', {
                          shouldDirty: true,
                        })
                      }}
                      register={register}
                      label="Play video"
                      labelPosition="end"
                      className="!flex-row !items-center"
                    />
                  </div>
                </div>

                {/* URL Input - changes label based on action type */}
                <FormInput
                  label={
                    actionButtonType === 'video' ? 'Video URL' : 'Button URL'
                  }
                  name="actionButton.url"
                  register={register}
                  validation={{
                    required: actionButtonEnabled ? 'URL is required' : false,
                  }}
                  error={errors.actionButton?.url?.message}
                  type="url"
                  placeholder={
                    actionButtonType === 'video'
                      ? 'Add video URL here...'
                      : 'Add URL here...'
                  }
                />

                {/* Target - only show for URL action type */}
                {actionButtonType === 'url' && (
                  <div className="space-y-2">
                    <label className="block text-md font-medium text-white">
                      Link Target
                    </label>
                    <div className="flex items-center gap-4">
                      <FormInput
                        type="radio"
                        name="actionButton.target"
                        id={`actionButton-target-blank-${id}`}
                        value="_blank"
                        defaultChecked
                        register={register}
                        label="Open in new tab"
                        labelPosition="end"
                        className="!flex-row !items-center"
                      />
                      <FormInput
                        type="radio"
                        name="actionButton.target"
                        id={`actionButton-target-self-${id}`}
                        value="_self"
                        register={register}
                        label="Open in current tab"
                        labelPosition="end"
                        className="!flex-row !items-center"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            {/* Image upload for backgroundImageUrl */}
            <div>
              <label
                htmlFor={`backgroundImageUrl-${id}`}
                className="block mb-2 text-white"
              >
                <div className="flex items-center text-md font-medium">
                  Background Image
                  {backgroundImageUrl && (
                    <IconButton
                      tooltipText="Download Image"
                      onClick={() => {
                        const extension = getFileExtension(backgroundImageUrl)
                        downloadFromUrl(
                          backgroundImageUrl,
                          `background-image.${extension}`,
                        )
                      }}
                      className="!p-1 rounded-full"
                      containerClassName="ml-2"
                    >
                      <DownloadIcon />
                    </IconButton>
                  )}
                </div>
                <p className="text-white/60 text-sm">
                  For best results, upload a JPG or PNG at 1216×704 resolution,
                  up to 300 KB in size.
                </p>
              </label>
              <ImageUploader
                id={`backgroundImageUrl-${id}`}
                value={backgroundImageUrl}
                loading={isGenerating || (value.isNewEntry ? false : undefined)}
                onChange={(url, file) => {
                  setValue('backgroundImageUrl', url, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                  setValue('backgroundImageFile', file, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }}
                error={errors.backgroundImageUrl?.message}
              />
            </div>
            {/* Separator with "or" keyword */}
            <div
              className={clsx(
                'h-full w-full flex flex-col md:flex-row items-center',
              )}
            >
              <Separator
                text="or"
                className="p-4"
                direction={isMobile ? 'horizontal' : 'vertical'}
              />
              <button
                type="button"
                className={clsx('btn  w-max btn-small btn-black', {
                  'btn-primary':
                    defaultValues.sourceType === 'Context' ||
                    defaultValues.sourceType === 'Banner',
                })}
                onClick={() => {
                  setOpen(true)
                }}
                disabled={isGenerating || isSubmitting}
                title="Generate Image"
              >
                {isGenerating && <AnimatedSpinner className="mr-2" />}
                Generate Image
              </button>
            </div>
          </div>

          {/* Video upload for backgroundVideoUrl */}
          <div>
            <label
              htmlFor={`backgroundVideoUrl-${id}`}
              className="block mb-2 text-white"
            >
              <div className="flex items-center text-md font-medium">
                Background Video
              </div>
              <p className="text-white/60 text-sm">
                Upload an MP4 video file for the background.
              </p>
            </label>
            <VideoUploader
              id={`backgroundVideoUrl-${id}`}
              value={backgroundVideoUrl || ''}
              loading={value.isNewEntry ? false : undefined}
              onChange={(url, file) => {
                setValue('backgroundVideoUrl', url, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
                setValue('backgroundVideoFile', file, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }}
              error={errors.backgroundVideoUrl?.message}
            />
          </div>

          {/* Companion Details Section */}
          <div className="space-y-4">
            <h3 className="text-md font-medium text-white">
              Companion Details
            </h3>

            {/* Enable Companion Checkbox */}
            <div className="ml-4">
              <FormInput
                type="checkbox"
                id={`enableCompanion-${id}`}
                name="enableCompanion"
                checked={companionEnabled}
                onChange={e => {
                  setCompanionEnabled(e.target.checked)
                  if (!e.target.checked) {
                    setCompanionData(undefined)
                    setCompanionSlug('')
                  }
                }}
                className="!flex-row !items-center !gap-2"
                label="Enable companion"
                labelPosition="end"
              />
            </div>

            {/* Show fields only when enabled */}
            {companionEnabled && (
              <div className="ml-4 space-y-4">
                {/* Slug Field with Refresh Button */}
                <div>
                  <label className="block mb-2 text-md font-medium text-white">
                    Companion Slug
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={companionSlug}
                      onChange={e => setCompanionSlug(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          void handleFetchCompanion()
                        }
                      }}
                      placeholder="Enter companion slug"
                      className="flex-1 px-3 py-2 tc-mv-question-field text-white rounded-2xl outline-none"
                    />
                    <IconButton
                      tooltipText="Fetch companion details"
                      onClick={handleFetchCompanion}
                      disabled={isFetchingCompanion || !companionSlug.trim()}
                      className="!p-2 rounded-full"
                    >
                      {isFetchingCompanion ? (
                        <AnimatedSpinner className="w-4 h-4" />
                      ) : (
                        <i className="fa-solid fa-rotate-right" />
                      )}
                    </IconButton>
                  </div>
                </div>

                {/* Companion Details Display */}
                {companionData && (
                  <div className="flex items-center space-x-2">
                    <video
                      preload="metadata"
                      muted
                      autoPlay
                      loop
                      playsInline
                      controlsList="nodownload"
                      disablePictureInPicture
                      className="w-20 h-20 sm:w-28 sm:h-28 object-cover rounded-full mr-4"
                      poster={companionData.imageUrl}
                      src={companionData.videoUrl || undefined}
                    />
                    <div className="flex-1 space-y-2">
                      <FormInput
                        label="Full Name"
                        name="fullName"
                        value={`${companionData.firstName} ${companionData.lastName}`}
                        type="text"
                        disabled
                      />
                      <FormInput
                        label="Job Title"
                        name="jobTitle"
                        value={companionData.jobTitle}
                        type="text"
                        disabled
                      />
                      <FormInput
                        type="checkbox"
                        id={`hideJobTitle-${id}`}
                        name="companion.hideJobTitle"
                        register={register}
                        className="ml-2 !flex-row !items-center !gap-2"
                        label="Hide Job Title"
                        labelPosition="end"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className={clsx('btn btn-small btn-black', {
              'btn-primary':
                defaultValues.sourceType === 'Context' ||
                defaultValues.sourceType === 'Banner',
            })}
            disabled={(!isDirty || isSubmitting) && !value.isNewEntry}
          >
            {isSubmitting && <AnimatedSpinner className="mr-2" />}
            Save Changes
          </button>
        </div>
      </form>
      <ImageGenerationModal
        open={open}
        onClose={handleClose}
        prompt={prompt}
        onChangePrompt={e => setPrompt(e.target.value)}
        onGenerate={handleGenerateImage}
        isGenerating={isGenerating}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}
